import { useState, useEffect, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '../domain/portfolio.types';
import { ProjectDialog } from './ProjectDialog';
import { usePortfolioQuery } from '../application/getPortfolio.query';
import { useReveal } from '../../../shared/lib/useReveal';
import { Preloader } from '../../../shared/ui';

export function ProjectsArchive() {
  const { data, isLoading } = usePortfolioQuery();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  
  const [theme, setTheme] = useState(() => {
    return (typeof localStorage !== 'undefined' && localStorage.getItem('portfolio-theme')) || 'dark';
  });

  useReveal(data);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.removeAttribute('data-vibe');
    try { localStorage.setItem('portfolio-theme', theme); } catch (e) {}
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <>
      <Preloader isDataLoaded={!!data && !isLoading} />
      {data && (
        <>
          <div className="bg-fx" />
          <div className="bg-scanlines" />
          <div className="bg-noise" />
          
          <div className="shell archive-page">
            <Link 
              to="/" 
          style={{
            position: 'fixed',
            top: 24,
            left: 24,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 20px',
            background: 'color-mix(in srgb, var(--bg) 80%, transparent)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--line-strong)',
            borderRadius: '999px',
            color: 'var(--fg)',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.1em',
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--line-strong)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
        >
          <span style={{ fontSize: 16 }}>←</span> HOME
        </Link>

        <button
          className={`floating-theme-toggle ${theme}`}
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          <svg 
            className="theme-icon" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <mask id="moon-mask-archive">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <circle className="moon-mask-circle" r="9" fill="black" />
            </mask>
            <circle 
              className="sun-core" 
              cx="12" cy="12" 
              mask="url(#moon-mask-archive)" 
              fill="currentColor"
            />
            <g className="sun-rays">
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </g>
            <g className="moon-stars" fill="currentColor" stroke="none">
              <circle cx="18" cy="6" r="1.5" />
              <circle cx="21" cy="11" r="1" />
            </g>
          </svg>
        </button>

        <section className="section" style={{ paddingTop: 140 }}>
          <div className="container">
            <div className="reveal" style={{ marginBottom: 56 }}>
              <div className="section-tag">Archive</div>
              <h1 className="section-title">
                All <span className="alt">Projects</span>.
              </h1>
              <p className="section-sub">A complete list of things I've built, shipped, and experimented with.</p>
            </div>
            
            <div className="projects-grid">
              {data.projects.map((p, i) => (
                <div 
                  className={`project-card reveal size-${p.size}`} 
                  key={i} 
                  style={{ transitionDelay: `${(i % 3) * 60}ms`, cursor: 'pointer' }}
                  onClick={(e) => { e.preventDefault(); setSelectedProject(p); }}
                >
                  <span className="slash"></span>
                  <div className="project-cover">
                    {p.details?.images && p.details.images.length > 0 ? (
                      <img src={p.details.images[0]} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }} />
                    ) : (
                      <div className="initials" style={{ '--initials-len': p.initials.length } as CSSProperties}>{p.initials}</div>
                    )}
                    {p.meta?.badge && <span className="badge">{p.meta.badge}</span>}
                    <span className="num">/ {String(i + 1).padStart(2, '0')}</span>
                    <span className="label">{p.meta?.label}</span>
                  </div>
                  <div className="project-meta">
                    <div className="project-tags">
                      {p.tags.map((t, j) => <span className="project-tag" key={j}>{t}</span>)}
                    </div>
                    <h3 className="project-title">
                      {p.title}
                      <span className="arrow">↗</span>
                    </h3>
                    <p className="project-blurb">{p.blurb}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <ProjectDialog project={selectedProject} onClose={() => setSelectedProject(null)} />
        </>
      )}
    </>
  );
}
